/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import CustomRadioGroup, { CustomRadioOption } from "@ui-components/RadioGroup/CustomRadioGroup";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { useAppSelector } from "@redux/hooks";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/IconDelete_KE.svg";
import { validateRtb } from "../../../../helpers/cloudPropagationValidation";
import {
  getRouteTablesAndNetworkInterfaces,
  getRouteTablesAndNetworkInterfacesForCluster,
  getCloudElementTagKeysForInstance,
  getCloudElementTagKeysForCluster,
  type CloudElementTagKeyType,
} from "../../request/request";
import {
  getFilteredRoutingTables, 
  getFilteredNetworkInterfaces, 
  getEmptyItemsMessage 
} from "../../../../helpers/filterFunctions";
import { 
  extractRouteTables, 
  extractNetworkInterfaces, 
  extractTransitGateways,
  extractVpcs,
  extractFullRouteTablesData,
  extractFullNetworkInterfacesData,
  extractFullTransitGatewaysData,
  extractTagKeyOptions,
} from "../../../../helpers/formattersShapedData";
import {
  clearEniSelection,
  removeRoutingTable,
  VRFFormData
} from "../../../../helpers/clearComponentStates";
import {
  DYNAMIC_TAG_MIN_VERSION,
  hasDynamicEniResolverNextHop,
  type NexthopRetryFormValues,
  type NexthopVerificationFormValues,
} from "../../../../helpers/cloudPropagationHelpers";
import TagNameDropdown from "./components/TagNameDropdown";
import NexthopRetrySettings from "./components/NexthopRetrySettings";
import NexthopVerificationSettings from "./components/NexthopVerificationSettings";

interface VRF {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[]; // Add routing tables array
  enis: string[]; // Add ENIs array
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection back
  inboundId?: number | null; // Add inboundId for Telco mode updates
  persistence: boolean; // Hold last-known route in the cloud when the peer goes down
  // Next-hop resolution retry — "Advanced settings → Retry" (CTX-181)
  nexthopRetryEnabled?: boolean;
  nexthopRetryIntervalSeconds?: number;
  nexthopRetryAttempts?: number;
}

interface InboundContentProps {
  values: any;
  setValues: (v: any) => void;
  isTelco?: boolean;
  reflectorId?: number; // Add reflectorId prop
  clusterId?: number; // Add clusterId prop
  getAccessTokenSilently?: () => Promise<string>; // Add token function prop
  bgpNeighborId?: number; // Add bgpNeighborId for delete operations
  propagationRuleId?: number | string; // Rule ID for operations (numeric or UUID)
  // deletedInboundId is set when the removed VRF already existed in the backend,
  // so the parent can defer the actual DELETE call until "Save" is pressed (CTX-154).
  onVrfDeleted?: (remainingVrfCount: number, deletedInboundId?: number) => void;
  // True when the associated reflector (standalone) or any instance in the cluster (cluster mode)
  // is below DYNAMIC_TAG_MIN_VERSION — disables the "Dynamic detection with tag" options (CTX-163).
  disableDynamicTagOptions?: boolean;
  // Same idea for the next-hop retry block, gated on its own minimum
  // (NEXTHOP_RETRY_MIN_VERSION) — renders the block inert (CTX-181).
  disableNexthopRetry?: boolean;
  // True when the reflector (or every instance of the cluster) is at or above
  // NEXTHOP_WATCH_MIN_VERSION: the per-rule Retry block is replaced by ONE
  // reflector-wide "Next-hop verification" block (CTX-347).
  useNexthopVerification?: boolean;
}

const DYNAMIC_TAG_VERSION_TOOLTIP = `Requires reflector version ${DYNAMIC_TAG_MIN_VERSION} or later`;
const DYNAMIC_TAG_DISABLED_LABEL = "Unsupported version";

const defaultVRF = (name: string): VRF => ({
  name,
  routeTargets: [],
  newRouteTarget: "",
  routingTable: "",
  nextHopEni: "",
  policySelection: "specific", // Changed from "dynamic" since it's now disabled
  nextHopSelection: "specific", // Changed from "dynamic" since it's now disabled
  routingTables: [], // Initialize empty array
  enis: [], // Initialize empty array
  expanded: true,
  selectedVpc: "", // Initialize VPC selection
  selectedRoutingTable: "", // Initialize routing table selection
  selectedEni: "", // Initialize ENI selection
  routingTableTagName: "", // Initialize routing table tag name
  routingTableTagValue: "", // Initialize routing table tag value
  nextHopTagName: "", // Initialize next hop tag name
  nextHopTagValue: "", // Initialize next hop tag value
  inboundId: null, // Initialize as null for new VRFs
  persistence: true, // Default: hold route on peer down (matches backend/naboo default)
});

const NEXT_HOP_DYNAMIC_TAG_TOOLTIP =
  "CTXR 2.0 will select the ENI with the lowest IP address based on the configured tag";

const InboundContent: React.FC<InboundContentProps> = ({
  propagationRuleId, 
  values, 
  setValues, 
  isTelco = false, 
  reflectorId, 
  clusterId, 
  getAccessTokenSilently,
  bgpNeighborId,
  onVrfDeleted,
  disableDynamicTagOptions = false,
  disableNexthopRetry = false,
  useNexthopVerification = false
}) => {
  const { tenantId } = useAppSelector((state: any) => state.selection);
  // State for API data
  const [routingTableOptions, setRoutingTableOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [eniOptions, setEniOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [vpcOptions, setVpcOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [routingTableTagOptions, setRoutingTableTagOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [nextHopTagOptions, setNextHopTagOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [isLoadingRoutingTableTags, setIsLoadingRoutingTableTags] = useState(false);
  const [isLoadingNextHopTags, setIsLoadingNextHopTags] = useState(false);
  
  // State for routing table validation errors (keyed by vrfIndex or 'enterprise')
  const [routingTableErrors, setRoutingTableErrors] = useState<{ [key: string]: string }>({});
  
  // Helper to get error key for routing table validation
  const getRoutingTableErrorKey = (vrfIndex?: number) => {
    return vrfIndex !== undefined ? `vrf-${vrfIndex}` : 'enterprise';
  };
  
  // Helper to get current routing table error
  const getRoutingTableError = (vrfIndex?: number) => {
    return routingTableErrors[getRoutingTableErrorKey(vrfIndex)] || '';
  };
  
  // Helper to check if current routing table value is valid (for button disabled state)
  const isCurrentRoutingTableValid = (value: string, vrfIndex?: number): boolean => {
    if (!value || !value.trim()) {
      return false;
    }
    
    const trimmed = value.trim();
    
    // Check if it's from dropdown (always valid)
    const isFromDropdown = routingTableOptions.some(opt => opt.value === trimmed);
    
    if (isFromDropdown) {
      return true;
    }
    
    // Validate custom values
    const validation = validateRtb(trimmed);
    return validation.valid;
  };
  
  // Ref to track if we're updating from parent to prevent circular updates
  const isUpdatingFromParentRef = useRef<boolean>(false);
  const previousValuesVrfsRef = useRef<string>('');
  
  // State for delete confirmation alert
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    vrfIndex: number | null;
    vrfName: string;
    inboundId: number | null;
  }>({
    isOpen: false,
    vrfIndex: null,
    vrfName: '',
    inboundId: null
  });
  
  // Store full API response data for filtering
  const [fullRouteTablesData, setFullRouteTablesData] = useState<any[]>([]);
  const [fullNetworkInterfacesData, setFullNetworkInterfacesData] = useState<any[]>([]);
  const [fullTransitGatewaysData, setFullTransitGatewaysData] = useState<any[]>([]);

  // Helper function to get displayLabel for routing table
  const getRoutingTableLabel = (elementId: string): string => {
    const item = fullRouteTablesData.find((rt: any) => rt.elementId === elementId);
    return item?.displayLabel || elementId;
  };

  // Helper function to get displayLabel for network interface or transit gateway
  const getNetworkInterfaceLabel = (elementId: string): string => {
    // Check if it's a transit gateway (starts with "tgw")
    if (elementId.toLowerCase().startsWith("tgw")) {
      const item = fullTransitGatewaysData.find((tgw: any) => tgw.elementId === elementId);
      return item?.displayLabel || elementId;
    }
    // Otherwise it's a network interface
    const item = fullNetworkInterfacesData.find((eni: any) => eni.elementId === elementId);
    return item?.displayLabel || elementId;
  };


  // VRF state for Telco mode - use passed values or create default
  const [vrfs, setVrfs] = useState<VRF[]>(() => {
    if (values.vrfs && values.vrfs.length > 0) {
      // Use the passed VRF values
      return values.vrfs;
    } else {
      // Create default VRF only if no values are passed
      return [defaultVRF("Default VRF")];
    }
  });


  // Fetch route tables and network interfaces from API
  useEffect(() => {
    const fetchOptions = async () => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId)) {
        return;
      }

      setIsLoadingOptions(true);
      try {
        if (!tenantId) return;
        const token = await getAccessTokenSilently();
        let response;

        if (clusterId) {
          // Fetch for cluster
          response = await getRouteTablesAndNetworkInterfacesForCluster(token, clusterId, tenantId);
        } else if (reflectorId) {
          // Fetch for standalone reflector
          response = await getRouteTablesAndNetworkInterfaces(token, reflectorId, tenantId);
        }

        if (response?.data) {
          // Store full data for filtering using centralized extractors
          const routeTablesData = extractFullRouteTablesData(response);
          const networkInterfacesData = extractFullNetworkInterfacesData(response);
          const transitGatewaysData = extractFullTransitGatewaysData(response);
          const vpcs = extractVpcs(response);

          // Store full data
          setFullRouteTablesData(routeTablesData);
          setFullNetworkInterfacesData(networkInterfacesData);
          setFullTransitGatewaysData(transitGatewaysData);
          setVpcOptions(vpcs);

          // Initially show all options (no VPC filter applied yet)
          const routeTables = extractRouteTables(response);
          const networkInterfaces = extractNetworkInterfaces(response);
          const transitGateways = extractTransitGateways(response);

          setRoutingTableOptions(routeTables);
          // Combine ENIs and Transit Gateways in the dropdown
          setEniOptions([...networkInterfaces, ...transitGateways]);
        }
      } catch (error) {
        console.error('Error fetching route tables and network interfaces:', error);
        // Fallback to empty arrays on error
        setRoutingTableOptions([]);
        setEniOptions([]);
        setVpcOptions([]);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [reflectorId, clusterId, getAccessTokenSilently, tenantId]);

  useEffect(() => {
    const fetchTagKeys = async (
      elementType: CloudElementTagKeyType,
      setOptions: (items: Array<{ value: string; label: string }>) => void,
      setLoading: (loading: boolean) => void
    ) => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId) || !tenantId) return;
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = clusterId
          ? await getCloudElementTagKeysForCluster(token, clusterId, tenantId, elementType)
          : await getCloudElementTagKeysForInstance(token, reflectorId!, tenantId, elementType);
        setOptions(extractTagKeyOptions(response));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchTagKeys("route_table", setRoutingTableTagOptions, setIsLoadingRoutingTableTags);
    /* Next hop resolves to an ENI, never a subnet, so the offered tag keys have to
       come from the interfaces — sourcing them from subnets let the operator pick a
       key the reflector's tag resolver could never match (CTX-287). */
    void fetchTagKeys("network_interface", setNextHopTagOptions, setIsLoadingNextHopTags);
  }, [reflectorId, clusterId, getAccessTokenSilently, tenantId]);

  // Update VRF state when values change (when rule details are loaded)
  useEffect(() => {
    if (isTelco) {
      // Check if values.vrfs exists and has data
      if (values.vrfs && Array.isArray(values.vrfs) && values.vrfs.length > 0) {
        // Check if values.vrfs actually changed (compare serialized versions)
        const currentVrfsStr = JSON.stringify(values.vrfs);
        if (currentVrfsStr === previousValuesVrfsRef.current) {
          // No change, skip update
          return;
        }
        
        // Mark that we're updating from parent
        isUpdatingFromParentRef.current = true;
        previousValuesVrfsRef.current = currentVrfsStr;
        
        // Convert "dynamic" to "specific" if it exists (since dynamic is now disabled)
        // Ensure all required VRF fields are present
        const convertedVrfs = values.vrfs.map((vrf: any) => ({
          name: vrf.name || 'Default VRF',
          routeTargets: Array.isArray(vrf.routeTargets) ? vrf.routeTargets : [],
          newRouteTarget: vrf.newRouteTarget || '',
          routingTable: vrf.routingTable || '',
          nextHopEni: vrf.nextHopEni || '',
          policySelection: vrf.policySelection || "specific",
          nextHopSelection: vrf.nextHopSelection || "specific",
          selectedRoutingTable: vrf.selectedRoutingTable || '',
          selectedEni: vrf.selectedEni || '',
          routingTableTagName: vrf.routingTableTagName || '',
          routingTableTagValue: vrf.routingTableTagValue || '',
          nextHopTagName: vrf.nextHopTagName || '',
          nextHopTagValue: vrf.nextHopTagValue || '',
          routingTables: Array.isArray(vrf.routingTables) ? vrf.routingTables : [],
          enis: Array.isArray(vrf.enis) ? vrf.enis : [],
          expanded: vrf.expanded !== undefined ? vrf.expanded : true,
          selectedVpc: vrf.selectedVpc || '',
          inboundId: vrf.inboundId || null,
          persistence: vrf.persistence ?? true,
          // Kept explicitly: this mapping is a whitelist, so a field missing here
          // is silently dropped when the parent re-hydrates the form (CTX-181).
          nexthopRetryEnabled: vrf.nexthopRetryEnabled,
          nexthopRetryIntervalSeconds: vrf.nexthopRetryIntervalSeconds,
          nexthopRetryAttempts: vrf.nexthopRetryAttempts,
        }));
        setVrfs(convertedVrfs);
      } else if (!values.vrfs || values.vrfs.length === 0) {
        // If no VRFs in values, but we're in Telco mode, ensure we have at least a default VRF
        // Only reset if we don't already have VRFs in state
        if (vrfs.length === 0 || (vrfs.length === 1 && vrfs[0].name === "Default VRF" && vrfs[0].routingTables.length === 0 && vrfs[0].enis.length === 0)) {
          // Don't reset if we already have meaningful VRF data
          return;
        }
      }
    }
  }, [values.vrfs, isTelco]);

  // Sync VRF state to parent (only when vrfs change internally, not when updating from parent)
  useEffect(() => {
    if (isTelco) {
      // Skip sync if we're currently updating from parent
      if (isUpdatingFromParentRef.current) {
        // Reset flag after skipping (use setTimeout to ensure it happens after React's state update cycle)
        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 0);
        return;
      }
      
      // Update top-level selectedVpc from first VRF's selectedVpc
      const firstVrfVpc = vrfs.length > 0 ? vrfs[0].selectedVpc : '';
      
      // Only update if the VRF array has actually changed
      const currentVrfsStr = JSON.stringify(vrfs);
      const currentValuesVrfsStr = JSON.stringify(values.vrfs || []);
      
      if (currentVrfsStr !== currentValuesVrfsStr) {
        // Functional update: this effect's `values` closure is stale (deps are
        // only [vrfs]), and spreading it here silently wiped any flat field
        // written between renders — e.g. the reflector-wide verification
        // settings (CTX-347). `prev` always carries the latest flat form.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValues((prev: any) => ({
          ...prev,
          vrfs,
          selectedVpc: firstVrfVpc
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vrfs, isTelco]);

  // Validate routing table input whenever it changes (for Telco mode VRFs)
  useEffect(() => {
    if (isTelco && vrfs.length > 0) {
      vrfs.forEach((vrf, idx) => {
        if (vrf.selectedRoutingTable && vrf.selectedRoutingTable.trim()) {
          validateAndSetRoutingTableError(vrf.selectedRoutingTable, idx);
        } else {
          // Clear error if empty
          const errorKey = getRoutingTableErrorKey(idx);
          setRoutingTableErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vrfs.map(vrf => vrf.selectedRoutingTable || '').join('|'), isTelco, routingTableOptions.length]);

  // Validate routing table input whenever it changes (for Enterprise mode)
  useEffect(() => {
    if (!isTelco) {
      if (values.selectedRoutingTable && values.selectedRoutingTable.trim()) {
        validateAndSetRoutingTableError(values.selectedRoutingTable);
      } else {
        // Clear error if empty
        const errorKey = getRoutingTableErrorKey();
        setRoutingTableErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.selectedRoutingTable, isTelco, routingTableOptions.length]);

  // Handle VPC prefilling when values change (for both Enterprise and Telco modes)
  useEffect(() => {
    if (values.selectedVpc && fullRouteTablesData.length > 0 && fullNetworkInterfacesData.length > 0) {
      // Update filtered options when VPC is pre-filled
      setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, values.selectedVpc));
      // Combine filtered ENIs with all Transit Gateways (TGWs are not VPC-specific)
      const filteredEnis = getFilteredNetworkInterfaces(fullNetworkInterfacesData, values.selectedVpc);
      const allTransitGateways = extractTransitGateways({ data: { data: { transit_gateways: fullTransitGatewaysData } } });
      setEniOptions([...filteredEnis, ...allTransitGateways]);
    }
  }, [values.selectedVpc, fullRouteTablesData, fullNetworkInterfacesData, fullTransitGatewaysData]);

  // Handle VPC prefilling for Telco mode VRFs
  useEffect(() => {
    if (isTelco && vrfs.length > 0 && fullRouteTablesData.length > 0 && fullNetworkInterfacesData.length > 0) {
      // Reset options when a new VRF is added (no VPC selected)
      // Only filter when a VRF has a VPC selected
      const vrfWithVpc = vrfs.find(vrf => vrf.selectedVpc);
      if (vrfWithVpc?.selectedVpc) {
        setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, vrfWithVpc.selectedVpc));
        // Combine filtered ENIs with all Transit Gateways (TGWs are not VPC-specific)
        const filteredEnis = getFilteredNetworkInterfaces(fullNetworkInterfacesData, vrfWithVpc.selectedVpc);
        const allTransitGateways = extractTransitGateways({ data: { data: { transit_gateways: fullTransitGatewaysData } } });
        setEniOptions([...filteredEnis, ...allTransitGateways]);
      } else {
        // Reset to show all options when no VPC is selected
        setRoutingTableOptions(extractRouteTables({ data: { data: { route_tables: fullRouteTablesData } } }));
        const allEnis = extractNetworkInterfaces({ data: { data: { network_interfaces: fullNetworkInterfacesData } } });
        const allTransitGateways = extractTransitGateways({ data: { data: { transit_gateways: fullTransitGatewaysData } } });
        setEniOptions([...allEnis, ...allTransitGateways]);
      }
    }
  }, [isTelco, vrfs, fullRouteTablesData, fullNetworkInterfacesData, fullTransitGatewaysData]);

  // Helper function to validate routing table input and update error state
  const validateAndSetRoutingTableError = (input: string, vrfIndex?: number): boolean => {
    if (!input || !input.trim()) {
      // Clear error if input is empty
      const errorKey = getRoutingTableErrorKey(vrfIndex);
      setRoutingTableErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
      return true; // Empty is valid (will be validated by required check)
    }
    
    const trimmedInput = input.trim();
    
    // Check if routing table is from dropdown (exists in options) - these are always valid
    const isFromDropdown = routingTableOptions.some(opt => opt.value === trimmedInput);
    if (isFromDropdown) {
      // Clear error if it's from dropdown
      const errorKey = getRoutingTableErrorKey(vrfIndex);
      setRoutingTableErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
      return true;
    }
    
    // If it's a custom value (not from dropdown), validate it using validateRtb
    const validation = validateRtb(trimmedInput);
    const errorKey = getRoutingTableErrorKey(vrfIndex);
    
    if (!validation.valid) {
      // Set error message
      setRoutingTableErrors(prev => ({
        ...prev,
        [errorKey]: "Invalid routing table format. Please enter a valid AWS, OCI, Azure, or GCP routing table ID."
      }));
      return false;
    }
    
    // Clear error if validation passes
    setRoutingTableErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
    return true;
  };
  
  // Helper function to validate all routing tables in an array
  const validateAllRoutingTables = (routingTables: string[]): string[] => {
    return routingTables.filter(rt => {
      if (!rt || !rt.trim()) return false;
      const trimmed = rt.trim();
      // Check if it's from dropdown
      const isFromDropdown = routingTableOptions.some(opt => opt.value === trimmed);
      if (isFromDropdown) return true;
      // Validate custom values
      return validateRtb(trimmed).valid;
    });
  };
  
  // Helper function to add routing table to array with validation
  const addRoutingTable = (routingTable: string, vrfIndex?: number) => {
    if (!routingTable || !routingTable.trim()) return;
    
    const trimmedRoutingTable = routingTable.trim();
    
    // Validate the routing table before adding - this is a final check
    // Even if the button wasn't disabled, we still validate here
    const isValid = validateAndSetRoutingTableError(trimmedRoutingTable, vrfIndex);
    if (!isValid) {
      // Clear the selection if validation fails
      if (isTelco && vrfIndex !== undefined) {
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex 
            ? { ...vrf, selectedRoutingTable: "" }
            : vrf
        ));
      } else {
        setValues({ ...values, selectedRoutingTable: "" });
      }
      return; // Don't add invalid routing table
    }
    
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, add to specific VRF
      if (!vrfs[vrfIndex].routingTables.includes(trimmedRoutingTable)) {
        // Validate all existing routing tables and add the new one
        const existingTables = vrfs[vrfIndex].routingTables || [];
        const validatedTables = validateAllRoutingTables([...existingTables, trimmedRoutingTable]);
        
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex 
            ? { ...vrf, routingTables: validatedTables, selectedRoutingTable: "" }
            : vrf
        ));
        // Clear error after successful add
        const errorKey = getRoutingTableErrorKey(vrfIndex);
        setRoutingTableErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      } else {
        // Already in list: clear input so user isn't confused
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex ? { ...vrf, selectedRoutingTable: "" } : vrf
        ));
      }
    } else {
      // For Enterprise mode, add to main values
      if (!values.routingTables?.includes(trimmedRoutingTable)) {
        // Validate all existing routing tables and add the new one
        const existingTables = values.routingTables || [];
        const validatedTables = validateAllRoutingTables([...existingTables, trimmedRoutingTable]);
        
        setValues({ 
          ...values, 
          routingTables: validatedTables,
          selectedRoutingTable: "" // Clear selection after adding
        });
        // Clear error after successful add
        const errorKey = getRoutingTableErrorKey();
        setRoutingTableErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      } else {
        // Already in list: clear input so user isn't confused
        setValues({ ...values, selectedRoutingTable: "" });
      }
    }
  };


  // Helper function to add ENI to array
  const addEni = (eni: string, vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, add to specific VRF
      if (eni && !vrfs[vrfIndex].enis.includes(eni)) {
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex 
            ? { ...vrf, enis: [...vrf.enis, eni], selectedEni: "" }
            : vrf
        ));
      }
    } else {
      // For Enterprise mode, add to main values
      if (eni && !values.enis?.includes(eni)) {
        setValues({ 
          ...values, 
          enis: [...(values.enis || []), eni],
          selectedEni: "" // Clear selection after adding
        });
      }
    }
  };


  // Helper function to add route target to VRF
  const addRouteTarget = (vrfIndex: number) => {
    const vrf = vrfs[vrfIndex];
    if (vrf.newRouteTarget.trim()) {
      const targets = vrf.newRouteTarget.split(',').map(rt => rt.trim()).filter(rt => rt);
      const newTargets = targets.filter(target => !vrf.routeTargets.includes(target));
      if (newTargets.length > 0) {
        setVrfs(vrfs.map((item, i) => 
          i === vrfIndex 
            ? { ...item, routeTargets: [...item.routeTargets, ...newTargets], newRouteTarget: "" }
            : item
        ));
      }
    }
  };

  // Handle delete VRF click - show confirmation alert
  const handleDeleteVrfClick = (vrfIndex: number) => {
    const vrf = vrfs[vrfIndex];
    if (vrf.inboundId) {
      setDeleteConfirmState({
        isOpen: true,
        vrfIndex,
        vrfName: vrf.name || 'VRF',
        inboundId: vrf.inboundId
      });
    } else {
      // If no inboundId, just remove from local state (new VRF that hasn't been saved)
      setVrfs(vrfs.filter((_, i) => i !== vrfIndex));
    }
  };

  // Handle delete confirmation. Removing a VRF here only updates local form
  // state — the actual DELETE call is deferred until the user presses "Save"
  // on the modal, so Cancel/close leaves the persisted VRF untouched (CTX-154).
  const handleDeleteConfirm = () => {
    if (deleteConfirmState.vrfIndex === null) {
      setDeleteConfirmState({
        isOpen: false,
        vrfIndex: null,
        vrfName: '',
        inboundId: null
      });
      return;
    }

    const remainingVrfs = vrfs.filter((_, i) => i !== deleteConfirmState.vrfIndex);
    setVrfs(remainingVrfs);

    // Notify parent component so it can clear any stale reference to this
    // inbound (e.g. ruleIds.inboundId) once no VRFs are left, and queue the
    // real delete to run when the modal is saved.
    if (onVrfDeleted) {
      onVrfDeleted(remainingVrfs.length, deleteConfirmState.inboundId ?? undefined);
    }

    setDeleteConfirmState({
      isOpen: false,
      vrfIndex: null,
      vrfName: '',
      inboundId: null
    });
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmState({
      isOpen: false,
      vrfIndex: null,
      vrfName: '',
      inboundId: null
    });
  };


  // Helper function to get existing ENI values from API response
  const getExistingEniValues = (values: any, vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, get ENIs from specific VRF
      const vrf = values.vrfs?.[vrfIndex];
      if (vrf?.enis && vrf.enis.length > 0) {
        return [vrf.enis[0]]; // Return first ENI value
      }
    } else {
      // For Enterprise mode, get ENIs from main values
      if (values.enis && values.enis.length > 0) {
        return [values.enis[0]]; // Return first ENI value
      }
    }
    return [];
  };

  // Helper function to check if VPC can be changed (validation)
  const canChangeVpc = (vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, check specific VRF
      const vrf = vrfs[vrfIndex];
      const hasRoutingTables = vrf.routingTables && vrf.routingTables.length > 0;
      const hasEnis = vrf.enis && vrf.enis.length > 0;
      return !hasRoutingTables && !hasEnis;
    } else {
      // For Enterprise mode, check main values
      const hasRoutingTables = values.routingTables && values.routingTables.length > 0;
      const hasEnis = values.enis && values.enis.length > 0;
      return !hasRoutingTables && !hasEnis;
    }
  };

  // Helper function to get validation message
  const getVpcValidationMessage = (vrfIndex?: number) => {
    // Only show validation message if VPC cannot be changed
    if (canChangeVpc(vrfIndex)) {
      return "";
    }
    
    if (isTelco && vrfIndex !== undefined) {
      const vrf = vrfs[vrfIndex];
      const hasRoutingTables = vrf.routingTables && vrf.routingTables.length > 0;
      const hasEnis = vrf.enis && vrf.enis.length > 0;
      
      if (hasRoutingTables && hasEnis) {
        return "Clear all routing tables and ENIs before changing VPC";
      } else if (hasRoutingTables) {
        return "Clear all routing tables before changing VPC";
      } else if (hasEnis) {
        return "Clear all ENIs before changing VPC";
      }
    } else {
      const hasRoutingTables = values.routingTables && values.routingTables.length > 0;
      const hasEnis = values.enis && values.enis.length > 0;
      
      if (hasRoutingTables && hasEnis) {
        return "Clear all routing tables and ENIs before changing VPC";
      } else if (hasRoutingTables) {
        return "Clear all routing tables before changing VPC";
      } else if (hasEnis) {
        return "Clear all ENIs before changing VPC";
      }
    }
    return "";
  };


  const handlePolicySelectionChange = (selection: string, vrfIndex?: number) => {
    const clearSpecific = selection === "dynamic"
      ? { routingTables: [] as string[] }
      : { routingTableTagName: "", routingTableTagValue: "" };

    if (isTelco && vrfIndex !== undefined) {
      setVrfs(
        vrfs.map((item, i) =>
          i === vrfIndex ? { ...item, policySelection: selection, ...clearSpecific } : item
        )
      );
    } else {
      setValues({ ...values, policySelection: selection, ...clearSpecific });
    }
  };

  // Helper function to set existing ENI values when switching to specific mode
  const handleNextHopSelectionChange = (selection: string, vrfIndex?: number) => {
    const clearTags =
      selection === "dynamic"
        ? { nextHopTagName: "", nextHopTagValue: "" }
        : selection === "specific" || selection === "dynamic_eni_resolver"
          ? { nextHopTagName: "", nextHopTagValue: "" }
          : {};

    if (selection === "specific") {
      const existingEnis = getExistingEniValues(values, vrfIndex);
      if (existingEnis.length > 0) {
        const firstEni = existingEnis[0];
        if (isTelco && vrfIndex !== undefined) {
          setVrfs(vrfs.map((item, i) => 
            i === vrfIndex 
              ? { ...item, nextHopSelection: selection, selectedEni: firstEni, enis: [firstEni], ...clearTags }
              : item
          ));
        } else {
          setValues({ 
            ...values, 
            nextHopSelection: selection,
            selectedEni: firstEni,
            enis: [firstEni],
            ...clearTags,
          });
        }
        return;
      }
    }

    if (selection === "dynamic") {
      if (isTelco && vrfIndex !== undefined) {
        setVrfs(vrfs.map((item, i) =>
          i === vrfIndex
            ? { ...item, nextHopSelection: selection, selectedEni: "", enis: [] }
            : item
        ));
      } else {
        setValues({
          ...values,
          nextHopSelection: selection,
          selectedEni: "",
          enis: [],
        });
      }
      return;
    }
    
    if (isTelco && vrfIndex !== undefined) {
      setVrfs(vrfs.map((item, i) => 
        i === vrfIndex 
          ? { ...item, nextHopSelection: selection, selectedEni: "", enis: [], ...clearTags }
          : item
      ));
    } else {
      setValues({ 
        ...values, 
        nextHopSelection: selection,
        selectedEni: "",
        enis: [],
        ...clearTags,
      });
    }
  };

  const renderRoutingTableTagNameField = (
    value: string,
    onChange: (tagName: string) => void
  ) => (
    <div>
      <div className="text-sm font-medium text-semantic-text-primary mb-2">
        Routing table tag name
      </div>
      <TagNameDropdown
        value={value}
        onChange={onChange}
        tagOptions={routingTableTagOptions}
        isLoadingTags={isLoadingRoutingTableTags}
      />
      <p className="text-xs text-semantic-text-secondary mt-2">
        Name used for dynamic detection
      </p>
    </div>
  );

  const renderNextHopTagNameField = (
    value: string,
    onChange: (tagName: string) => void
  ) => (
    <div>
      <div className="text-sm font-medium text-semantic-text-primary mb-2">
        Next-hop tag name
      </div>
      {/* Custom values are allowed here and nowhere else in this form: the tag
          resolver matches whatever key the operator names, and a key can exist on
          an ENI the discovery pass has not written yet (or be typed ahead of the
          interface being tagged). The routing-table field stays list-only. */}
      <TagNameDropdown
        value={value}
        onChange={onChange}
        tagOptions={nextHopTagOptions}
        isLoadingTags={isLoadingNextHopTags}
        allowCustomTagValues
        tagEmptyMessage="No tags found — type a key to use it"
      />
      {/* Names the namespace explicitly: this list and the routing-table one above
          are drawn from different element types and are not interchangeable. */}
      <p className="text-xs text-semantic-text-secondary mt-2">
        Tag key present on the network interfaces to use as next hop, or type your own
      </p>
    </div>
  );

  // Helper function to create policy selection options with body content
  const createPolicySelectionOptions = (
    vrf: VRF,
    idx: number,
    routingTableOptions: Array<{ value: string; label: string }>,
    isLoadingOptions: boolean,
    addRoutingTable: (routingTable: string, vrfIndex?: number) => void,
    removeRoutingTable: (routingTable: string, isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setVrfs: any,
    isTelco: boolean,
    values: any,
    setValues: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific routing tables",
      bodyContent: (
                    <div className="mt-4">
                      <div className="w-full mb-2">
                        <GenericSearchableDropdown
                          items={(routingTableOptions || []).filter(opt => !vrf.routingTables.includes(opt.value))}
                          value={String(vrf.selectedRoutingTable ?? "")}
                          onSelect={(v) => addRoutingTable(v, idx)}
                          onCustomValue={(v) => addRoutingTable(v, idx)}
                          defaultLabel="Select..."
                          widthClass="w-full"
                          className="w-full"
                          minWidthClass=""
                          maxWidthClass=""
                          disabled={isLoadingOptions || !vrf.selectedVpc}
                          emptyItemsMessage={getEmptyItemsMessage(vrf.selectedVpc || "", isLoadingOptions, "No routing tables available")}
                          allowCustomValues={true}
                        />
                        {getRoutingTableError(idx) && (
                          <GenericInlineAlert variant="warning" className="mt-1">
                            {getRoutingTableError(idx)}
                          </GenericInlineAlert>
                        )}
                      </div>
                      {/* Display routing tables as tags */}
                      {vrf.routingTables.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {vrf.routingTables.map((routingTable, rtIdx) => (
                            <div key={rtIdx} className="flex items-center">
                              <GenericTag
                                label={getRoutingTableLabel(routingTable)}
                                variant="default"
                                removable={true}
                                onRemove={() => removeRoutingTable(routingTable, isTelco, idx, vrfs, setVrfs, values, setValues)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      disabled: disableDynamicTagOptions,
      tooltipText: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : undefined,
      disabledTagLabel: DYNAMIC_TAG_DISABLED_LABEL,
      bodyContent: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {renderRoutingTableTagNameField(
                        vrf.routingTableTagName ?? "",
                        (v) =>
                          setVrfs(
                            vrfs.map((item, i) =>
                              i === idx ? { ...item, routingTableTagName: v } : item
                            )
                          )
                      )}
                      <GenericInput
                        label="Routing table tag value"
                        value={vrf.routingTableTagValue ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, routingTableTagValue: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Value the tag should match"
                        className="text-body"
                      />
                    </div>
      )
    }
  ];

  // Helper function to create next hop selection options with body content
  const createNextHopSelectionOptions = (
    vrf: VRF,
    idx: number,
    eniOptions: Array<{ value: string; label: string }>,
    isLoadingOptions: boolean,
    getExistingEniValues: (values: any, vrfIndex?: number) => string[],
    clearEniSelection: (isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setVrfs: any,
    isTelco: boolean,
    values: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific ENI or TGW",
      bodyContent: (
        <div className="mt-4">
          <div className="flex items-end gap-2 w-full">
            <GenericSearchableDropdown
              items={eniOptions || []}
              value={(() => {
                const existingEnis = getExistingEniValues(values, idx);
                // If we have existing ENIs, use the first one as value; otherwise use current selection
                return existingEnis.length > 0 ? existingEnis[0] : String(vrf.selectedEni ?? "");
              })()}
              defaultLabel="Select..."
              onSelect={(v) => {
                // Automatically set the ENI when selected (only one ENI supported)
                setVrfs(vrfs.map((item, i) => i === idx ? { 
                  ...item, 
                  selectedEni: v,
                  enis: v ? [v] : [] // Set only one ENI
                } : item));
              }}
              widthClass="w-full"
              className="flex-1"
              minWidthClass=""
              maxWidthClass=""
              disabled={isLoadingOptions || !vrf.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(vrf.selectedVpc || "", isLoadingOptions, "No network interfaces available")}
              allowCustomValues={true}
            />
            <GenericButton
              label="×"
              customHeight="35px"
              variant="secondary"
              shape="ghost"
              size="sm"
              onClick={() => clearEniSelection(isTelco, idx, vrfs, setVrfs, values, setValues)}
              disabled={!vrf.selectedEni && (!vrf.enis || vrf.enis.length === 0)}
            />
          </div>
        </div>
      )
    },
    {
      value: "dynamic_eni_resolver",
      label: "Dynamic ENI Resolver",
      bodyContent: (
        <div className="mt-4 text-body text-semantic-text-secondary">
          The next-hop ENI is resolved automatically and in real time from the
          BGP next-hop of the best path. No specific ENI or tag is required.
        </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      disabled: disableDynamicTagOptions,
      tooltipText: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : NEXT_HOP_DYNAMIC_TAG_TOOLTIP,
      disabledTagLabel: DYNAMIC_TAG_DISABLED_LABEL,
      bodyContent: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {renderNextHopTagNameField(
                        vrf.nextHopTagName ?? "",
                        (v) =>
                          setVrfs(
                            vrfs.map((item, i) =>
                              i === idx ? { ...item, nextHopTagName: v } : item
                            )
                          )
                      )}
                      <GenericInput
                        label="Next-hop tag value"
                        value={vrf.nextHopTagValue ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, nextHopTagValue: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Value the tag should match"
                        className="text-body"
                      />
        </div>
      )
    }
  ];

  // Helper function to create policy selection options with body content for Enterprise mode
  const createEnterprisePolicySelectionOptions = (
    values: any,
    routingTableOptions: Array<{ value: string; label: string }>,
    isLoadingOptions: boolean,
    addRoutingTable: (routingTable: string, vrfIndex?: number) => void,
    removeRoutingTable: (routingTable: string, isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setValues: any,
    isTelco: boolean,
    vrfs: any,
    setVrfs: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific routing tables",
      bodyContent: (
        <div className="mt-4">
          <div className="w-full mb-2">
            <GenericSearchableDropdown
              items={(routingTableOptions || []).filter(opt => !(values.routingTables || []).includes(opt.value))}
              value={String(values.selectedRoutingTable ?? "")}
              onSelect={(v) => addRoutingTable(v)}
              onCustomValue={(v) => addRoutingTable(v)}
              defaultLabel="Select..."
              widthClass="w-full"
              className="w-full"
              minWidthClass=""
              maxWidthClass=""
              disabled={isLoadingOptions || !values.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(values.selectedVpc || "", isLoadingOptions, "No routing tables available")}
              allowCustomValues={true}
            />
            {getRoutingTableError() && (
              <GenericInlineAlert variant="warning" className="mt-1">
                {getRoutingTableError()}
              </GenericInlineAlert>
            )}
          </div>
          {/* Display routing tables as tags */}
          {values.routingTables && values.routingTables.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {values.routingTables.map((routingTable: string, index: number) => (
                <div key={index} className="flex items-center">
                  <GenericTag
                    label={getRoutingTableLabel(routingTable)}
                    variant="default"
                    removable={true}
                    onRemove={() => removeRoutingTable(routingTable, isTelco, 0, vrfs, setVrfs, values, setValues)}
                  />
                </div>
              ))}
                    </div>
                  )}
        </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      disabled: disableDynamicTagOptions,
      tooltipText: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : undefined,
      disabledTagLabel: DYNAMIC_TAG_DISABLED_LABEL,
      bodyContent: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {renderRoutingTableTagNameField(
            values.routingTableTagName ?? "",
            (v) => setValues({ ...values, routingTableTagName: v })
          )}
          <GenericInput
            label="Routing table tag value"
            value={values.routingTableTagValue ?? ""}
            onChange={(v) => setValues({ ...values, routingTableTagValue: v })}
            placeholder="E.g. RT"
            helperText="Value the tag should match"
            className="text-body"
          />
        </div>
      )
    }
  ];

  // Helper function to create next hop selection options with body content for Enterprise mode
  const createEnterpriseNextHopSelectionOptions = (
    values: any,
    eniOptions: Array<{ value: string; label: string }>,
    isLoadingOptions: boolean,
    getExistingEniValues: (values: any, vrfIndex?: number) => string[],
    clearEniSelection: (isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setValues: any,
    isTelco: boolean,
    vrfs: any,
    setVrfs: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific ENI or TGW",
      bodyContent: (
                    <div className="mt-4">
                      <div className="flex items-end gap-2 w-full">
                        <GenericSearchableDropdown
                          items={eniOptions || []}
                          value={(() => {
                const existingEnis = getExistingEniValues(values);
                            // If we have existing ENIs, use the first one as value; otherwise use current selection
                return existingEnis.length > 0 ? existingEnis[0] : String(values.selectedEni ?? "");
                          })()}
                          defaultLabel="Select..."
                          onSelect={(v) => {
                            // Automatically set the ENI when selected (only one ENI supported)
                setValues({ 
                  ...values, 
                              selectedEni: v,
                              enis: v ? [v] : [] // Set only one ENI
                });
                          }}
                          widthClass="w-full"
                          className="flex-1"
                          minWidthClass=""
                          maxWidthClass=""
              disabled={isLoadingOptions || !values.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(values.selectedVpc || "", isLoadingOptions, "No network interfaces available")}
                          allowCustomValues={true}
                        />
                        <GenericButton
                          label="×"
                          customHeight="35px"
                          variant="secondary"
                          shape="ghost"
                          size="sm"
              onClick={() => clearEniSelection(isTelco, 0, vrfs, setVrfs, values, setValues)}
              disabled={!values.selectedEni && (!values.enis || values.enis.length === 0)}
                        />
                      </div>
                    </div>
      )
    },
    {
      value: "dynamic_eni_resolver",
      label: "Dynamic ENI Resolver",
      bodyContent: (
        <div className="mt-4 text-body text-semantic-text-secondary">
          The next-hop ENI is resolved automatically and in real time from the
          BGP next-hop of the best path. No specific ENI or tag is required.
        </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      disabled: disableDynamicTagOptions,
      tooltipText: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : NEXT_HOP_DYNAMIC_TAG_TOOLTIP,
      disabledTagLabel: DYNAMIC_TAG_DISABLED_LABEL,
      bodyContent: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {renderNextHopTagNameField(
            values.nextHopTagName ?? "",
            (v) => setValues({ ...values, nextHopTagName: v })
          )}
          <GenericInput
            label="Next-hop tag value"
            value={values.nextHopTagValue ?? ""}
            onChange={(v) => setValues({ ...values, nextHopTagValue: v })}
            placeholder="E.g. RT"
            helperText="Value the tag should match"
            className="text-body"
          />
        </div>
      )
    }
  ];

  // The verification block only has something to verify when a next-hop is
  // resolved live from BGP — with a pinned ENI or a tag there is nothing to
  // re-resolve, so the block stays hidden instead of offering an inert cadence.
  const showNexthopVerification =
    useNexthopVerification &&
    hasDynamicEniResolverNextHop({ nextHopSelection: values.nextHopSelection, vrfs }, isTelco);

  // Telco logic
  if (isTelco) {
    return (
      <div className="flex flex-col gap-6 text-body">
        <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
          Configure how IPv4 routes from this BGP session are propagated to cloud routing tables. Specify routing tables and next-hop preferences
        </div>
        {/* VRF configuration */}
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-semantic-text-primary text-body">VRF configuration</div>
          <GenericButton label="+ Add VRF" size="sm" variant="secondary" shape="ghost" onClick={() => setVrfs([...vrfs, defaultVRF(`Default VRF${vrfs.length > 0 ? ` ${vrfs.length + 1}` : ""}`)])} />
        </div>
        <div className="flex flex-col gap-4">
          {vrfs.map((vrf, idx) => {
            const vrfBody = (
              <div className="p-4 flex flex-col gap-4 ">
                <GenericInput
                  label="VRF name"
                  value={vrf.name}
                  onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, name: v } : item))}
                  className="text-body text-semantic-text-primary"
                />
                <div className="pb-5 group relative">
                  <div className=" text-semantic-text-primary mb-2 text-body">VPC</div>
                  <GenericSearchableDropdown
                    items={vpcOptions || []}
                    value={String(vrf.selectedVpc ?? "")}
                    onSelect={(v) => {
                      if (canChangeVpc(idx)) {
                        setVrfs(vrfs.map((item, i) => i === idx ? { 
                          ...item, 
                          selectedVpc: v,
                          routingTables: [],
                          enis: [],
                          selectedRoutingTable: "",
                          selectedEni: ""
                        } : item));
                        setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, v));
                        const filteredEnis = getFilteredNetworkInterfaces(fullNetworkInterfacesData, v);
                        const allTransitGateways = extractTransitGateways({ data: { data: { transit_gateways: fullTransitGatewaysData } } });
                        setEniOptions([...filteredEnis, ...allTransitGateways]);
                        setValues({ ...values, selectedVpc: v });
                      }
                    }}
                    defaultLabel="Select VPC..."
                    widthClass="w-full"
                    className="w-full"
                    minWidthClass=""
                    maxWidthClass=""
                    disabled={isLoadingOptions || !canChangeVpc(idx)}
                    emptyItemsMessage={isLoadingOptions ? "Loading..." : "No VPCs available"}
                    allowCustomValues={true}
                  />
                  {getVpcValidationMessage(idx) && (
                    <p className="text-xs text-semantic-text-warning mt-1">{getVpcValidationMessage(idx)}</p>
                  )}
                </div>
                <div>
                  <div className="text-body text-semantic-text-primary mb-1">Route targets</div>
                  <div className=" gap-2 mb-2 flex flex-col">
                    <div className="flex items-end gap-2">
                      <GenericInput
                        value={vrf.routeTargets.length > 0 ? vrf.routeTargets[0] : vrf.newRouteTarget}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { 
                          ...item, 
                          newRouteTarget: v,
                          routeTargets: v ? [v] : [] // Update the routeTargets array with the new value
                        } : item))}
                        placeholder="E.g. 65000:100"
                        className="text-body w-full flex-1"
                      />
                      {/* <GenericButton
                        label="+"
                        customHeight="35px"
                        variant="secondary"
                        shape="ghost"
                        size="sm"
                        onClick={() => addRouteTarget(idx)}
                        disabled={!vrf.newRouteTarget.trim()}
                      /> */}
                    </div>
                    {/* <span className=" ml-2 text-semantic-text-secondary text-xs">Comma-separate list of route targets</span> */}
                  </div>
                </div>
                <div>
                  <div className=" text-semantic-text-primary mb-2 text-body-large">Routing tables</div>
                  <div className="text-semantic-text-secondary text-body-small mb-2">How do you want to define your routing tables?</div>
                  <GenericCard
                    variant="primary"
                    shadow="flat"
                    isCollapsible={false}
                    hasBorder={true}
                    bodyContent={
                      <CustomRadioGroup
                        name={`policySelection-${idx}`}
                        options={createPolicySelectionOptions(
                          vrf,
                          idx,
                          routingTableOptions,
                          isLoadingOptions,
                          addRoutingTable,
                          removeRoutingTable,
                          setVrfs,
                          isTelco,
                          values,
                          setValues
                        )}
                        value={vrf.policySelection}
                        onChange={(v) => handlePolicySelectionChange(v, idx)}
                      />
                    }
                  />
                </div>
                <div>
                  <div className=" text-semantic-text-primary mb-2 text-body-large">Next-hop settings</div>
                  <div className="text-semantic-text-secondary text-small mb-2">How do you want to define the next hop?</div>
                  <GenericCard
                    variant="primary"
                    shadow="flat"
                    isCollapsible={false}
                    hasBorder={true}
                    bodyContent={
                      <>
                        <CustomRadioGroup
                          name={`nextHopSelection-${idx}`}
                          options={createNextHopSelectionOptions(
                            vrf,
                            idx,
                            eniOptions,
                            isLoadingOptions,
                            getExistingEniValues,
                            clearEniSelection,
                            setVrfs,
                            isTelco,
                            values
                          )}
                          value={vrf.nextHopSelection}
                          onChange={(v) => handleNextHopSelectionChange(v, idx)}
                        />
                        {/* Per-VRF Retry disappears on ≥ NEXTHOP_WATCH_MIN_VERSION: the
                            verification cadence is per CPR, configured once for the
                            whole rule below the VRF list (CTX-347). */}
                        {!useNexthopVerification && (
                          <div className="mt-6">
                            <NexthopRetrySettings
                              values={vrf}
                              disabled={disableNexthopRetry}
                              onChange={(patch: NexthopRetryFormValues) =>
                                setVrfs(vrfs.map((item, i) => i === idx ? { ...item, ...patch } : item))
                              }
                            />
                          </div>
                        )}
                      </>
                    }
                  />
                </div>
                <div>
                  <div className="text-semantic-text-primary mb-2 text-body-large">Route persistence</div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="text-semantic-text-secondary text-body-small">
                      Keep the last-known route in the cloud if the peer goes down. Turn off to
                      withdraw the route from the cloud when the session drops. (An explicit BGP
                      withdraw always removes the route.)
                    </div>
                    <GenericSwitch
                      checked={vrf.persistence ?? true}
                      onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, persistence: v } : item))}
                    />
                  </div>
                </div>
              </div>
            );
            return (
              <GenericCard
                variant="primary"
                shadow="flat"
                key={idx}
                isCollapsible
                hasBorder={true}
                title={vrf.name}
                headerContent={vrf.name || "Default VRF"}
                bodyContent={vrfBody}
                initialExpanded={false}
                rightContent={vrfs.length > 1 || (vrf.inboundId && vrfs.length === 1) ? (
                  <GenericButton
                    isIconOnly
                    icon={<IconDelete className="w-4 h-4" />}
                    iconPosition="right"
                    size="sm"
                    variant="danger"
                    shape="filled"
                    onClick={() => handleDeleteVrfClick(idx)}
                  />
                ) : undefined}
                actionsContent={true}
              />
            );
          })}
        </div>

        {/* Per-CPR next-hop verification (CTX-347): one cadence per rule
            (every VRF row carries the same value), so exactly ONE block for
            the whole rule — never per VRF. Values live on the flat form.
            Shown once any VRF resolves its next-hop dynamically. */}
        {showNexthopVerification && (
          <GenericCard
            variant="primary"
            shadow="flat"
            isCollapsible={false}
            hasBorder={true}
            bodyContent={
              <NexthopVerificationSettings
                values={values}
                onChange={(patch: NexthopVerificationFormValues) =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  setValues((prev: any) => ({ ...prev, ...patch }))
                }
              />
            }
          />
        )}

        {/* Delete confirmation alert */}
        <GenericAlert
          isOpen={deleteConfirmState.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete VRF"
          description={`Are you sure you want to remove the VRF "${deleteConfirmState.vrfName}"? This won't be applied until you click Save.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="error"
        />
      </div>
    );
  }


  // Enterprise logic
  return (
    <div className="flex flex-col gap-6 text-body">
      <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
        Configure how IPv4 routes from this BGP session are propagated to cloud routing tables. Specify routing tables and next-hop preferences
      </div>
      <div className="pb-4 group relative">
        <div className=" text-semantic-text-primary mb-2 text-body">VPC</div>
        <GenericSearchableDropdown
          items={vpcOptions || []}
          value={String(values.selectedVpc ?? "")}
          onSelect={(v) => {
            if (canChangeVpc()) {
              setValues({ ...values, selectedVpc: v });
              setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, v));
              const filteredEnis = getFilteredNetworkInterfaces(fullNetworkInterfacesData, v);
              const allTransitGateways = extractTransitGateways({ data: { data: { transit_gateways: fullTransitGatewaysData } } });
              setEniOptions([...filteredEnis, ...allTransitGateways]);
            }
          }}
          defaultLabel="Select VPC..."
          widthClass="w-full"
          className="w-full"
          minWidthClass=""
          maxWidthClass=""
          disabled={isLoadingOptions || !canChangeVpc()}
          emptyItemsMessage={isLoadingOptions ? "Loading..." : "No VPCs available"}
          allowCustomValues={true}
        />
        {getVpcValidationMessage() && (
          <p className="text-xs text-semantic-text-warning mt-1">{getVpcValidationMessage()}</p>
        )}
      </div>
      <div>
        <div className=" text-semantic-text-primary mb-2 text-body-large">Routing Tables</div>
        <div className="text-semantic-text-secondary text-body-small mb-2">How do you want to define your routing tables?</div>
        <GenericCard
          variant="primary"
          shadow="flat"
          isCollapsible={false}
          hasBorder={true}
          bodyContent={
            <CustomRadioGroup
              name="policySelection"
              options={createEnterprisePolicySelectionOptions(
                values,
                routingTableOptions,
                isLoadingOptions,
                addRoutingTable,
                removeRoutingTable,
                setValues,
                isTelco,
                vrfs,
                setVrfs
              )}
              value={values.policySelection}
              onChange={(v) => handlePolicySelectionChange(v)}
            />
          }
        />
      </div>
      <div>
        <div className=" text-semantic-text-primary mb-2 text-body-large">Next-hop settings</div>
        <div className="text-semantic-text-secondary text-body-small mb-2">How do you want to define the next hop?</div>
        <GenericCard
          variant="primary"
          shadow="flat"
          isCollapsible={false}
          hasBorder={true}
          bodyContent={
            <>
              <CustomRadioGroup
                name="nextHopSelection"
                options={createEnterpriseNextHopSelectionOptions(
                  values,
                  eniOptions,
                  isLoadingOptions,
                  getExistingEniValues,
                  clearEniSelection,
                  setValues,
                  isTelco,
                  vrfs,
                  setVrfs
                )}
                value={values.nextHopSelection}
                onChange={(v) => handleNextHopSelectionChange(v)}
              />
              {/* Same two blocks as Telco, on the same gates: the legacy Retry
                  below NEXTHOP_WATCH_MIN_VERSION, and the per-CPR verification
                  only while this rule resolves its next-hop dynamically. */}
              {!useNexthopVerification && (
                <div className="mt-6">
                  <NexthopRetrySettings
                    values={values}
                    disabled={disableNexthopRetry}
                    onChange={(patch: NexthopRetryFormValues) => setValues({ ...values, ...patch })}
                  />
                </div>
              )}
              {showNexthopVerification && (
                <div className="mt-6">
                  <NexthopVerificationSettings
                    values={values}
                    onChange={(patch: NexthopVerificationFormValues) =>
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      setValues((prev: any) => ({ ...prev, ...patch }))
                    }
                  />
                </div>
              )}
            </>
          }
        />
      </div>
      <div>
        <div className="text-semantic-text-primary mb-2 text-body-large">Route persistence</div>
        <div className="flex items-start justify-between gap-4">
          <div className="text-semantic-text-secondary text-body-small">
            Keep the last-known route in the cloud if the peer goes down. Turn off to
            withdraw the route from the cloud when the session drops. (An explicit BGP
            withdraw always removes the route.)
          </div>
          <GenericSwitch
            checked={values.persistence ?? true}
            onChange={(v) => setValues({ ...values, persistence: v })}
          />
        </div>
      </div>
    </div>
  );
};

export default InboundContent;
